import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  FileText,
  Globe,
  Lock,
  Paperclip,
  Sparkles,
  TriangleAlert,
  X,
} from "lucide-react";
import { Button } from "../../ui/button";
import { useTranslation } from "../../../hooks/useTranslation";

const MAX_DESCRIPTION = 2000;
const MAX_FILES = 5;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_INLINE_TEXT = 200000;

// Ritmo del placeholder animato (macchina da scrivere).
const TYPE_MS = 26; // battitura
const DELETE_MS = 11; // cancellazione (più rapida, 3 caratteri a colpo)
const HOLD_MS = 2400; // pausa a frase completa
const GAP_MS = 450; // pausa a campo vuoto prima dell'esempio successivo

// Stessa validazione del ramo automatico: dominio con TLD, path opzionale.
const isValidDomain = (value) =>
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(\/.*)?$/i.test(
    value.trim().replace(/^https?:\/\//i, "")
  );

// Riconoscimento URL scritti a mano nel testo libero (es. "dai un'occhiata a
// mionegozio.it"): più severo di isValidDomain — il TLD deve essere solo
// lettere, per non scambiare per un sito numeri tipo "3.5" o versioni "v2.1".
const URL_TOKEN_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\/\S*)?$/i;

const extractUrlFromText = (text) => {
  const tokens = text.split(/\s+/);
  for (const raw of tokens) {
    const token = raw.replace(/^[("'“‘]+|[)."'”’,;:!?]+$/g, "");
    if (!token || token.includes("@") || !URL_TOKEN_REGEX.test(token)) {
      continue;
    }
    return token.replace(/^https?:\/\//i, "");
  }
  return "";
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)}MB`;
};

/**
 * Entry prompt-first dell'onboarding v2 — hero a tutta pagina sul mesh
 * gradient del loader: card bianca con textarea, "+" alla Lovable in basso
 * a sinistra (sito e PDF) e CTA in basso a destra.
 *
 * inShell: true quando la pagina vive dentro il MainLayout (sidebar) — il
 * wrapper compensa i padding del layout come fa CreationLoader.
 */
const PromptIntro = ({
  onSubmit,
  onManual,
  inShell = false,
  // Prefill del draft (post-registrazione o ritorno sulla landing): il testo
  // e il sito tornano al loro posto, l'invio resta un gesto dell'utente.
  initialDraft = null,
  initialFiles = [],
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [description, setDescription] = useState(
    initialDraft?.description || ""
  );
  const [placeholder, setPlaceholder] = useState("");
  const [siteOpen, setSiteOpen] = useState(!!initialDraft?.websiteUrl);
  const [websiteUrl, setWebsiteUrl] = useState(initialDraft?.websiteUrl || "");
  // Finché l'utente non tocca il campo sito a mano (toggle, digitazione o
  // rimozione), resta libero di essere popolato/svuotato in automatico
  // quando scrive un URL nel testo della descrizione.
  const [siteUserManaged, setSiteUserManaged] = useState(
    !!initialDraft?.websiteUrl
  );
  const [files, setFiles] = useState(initialFiles || []);
  const [fileError, setFileError] = useState("");

  // Placeholder animato: scrive un esempio a macchina, pausa, cancella e
  // passa al successivo, in loop. Con "riduci movimento" resta statico.
  useEffect(() => {
    const examples = [
      t("onboardingWizard.introPlaceholder"),
      t("onboardingWizard.introSuggestion1"),
      t("onboardingWizard.introSuggestion2"),
      t("onboardingWizard.introSuggestion3"),
    ].filter(Boolean);

    if (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ||
      examples.length === 0
    ) {
      setPlaceholder(examples[0] || "");
      return undefined;
    }

    let index = 0;
    let pos = 0;
    let deleting = false;
    let timer;

    const tick = () => {
      const full = examples[index];
      if (!deleting) {
        pos += 1;
        const atEnd = pos >= full.length;
        setPlaceholder(full.slice(0, pos) + (atEnd ? "" : "▍"));
        if (atEnd) {
          deleting = true;
          timer = setTimeout(tick, HOLD_MS);
          return;
        }
        timer = setTimeout(tick, TYPE_MS);
      } else {
        pos = Math.max(0, pos - 3);
        setPlaceholder(full.slice(0, pos) + (pos > 0 ? "▍" : ""));
        if (pos === 0) {
          deleting = false;
          index = (index + 1) % examples.length;
          timer = setTimeout(tick, GAP_MS);
          return;
        }
        timer = setTimeout(tick, DELETE_MS);
      }
    };

    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, [t]);

  // Riconoscimento automatico del sito dal testo: se l'utente scrive un URL
  // nella descrizione ("dai un'occhiata a mionegozio.it...") e non ha ancora
  // toccato di persona il campo sito, lo mettiamo noi nell'input dedicato —
  // resta visibile e rimovibile, così chi "si ispira" a un sito senza
  // volerne usare i contenuti può semplicemente cancellarlo.
  useEffect(() => {
    if (siteUserManaged) return;
    const detected = extractUrlFromText(description);
    if (detected) {
      setWebsiteUrl(detected);
      setSiteOpen(true);
    } else if (siteOpen) {
      setSiteOpen(false);
      setWebsiteUrl("");
    }
  }, [description, siteUserManaged, siteOpen]);

  const cleanedUrl = websiteUrl.trim().replace(/^https?:\/\//i, "");
  const urlValid = cleanedUrl.length > 0 && isValidDomain(cleanedUrl);
  const urlInvalid = cleanedUrl.length > 3 && !urlValid;
  const canSubmit = description.trim().length >= 10 || (siteOpen && urlValid);

  const handleFiles = async (fileList) => {
    setFileError("");
    const added = [];
    let count = files.length;
    for (const file of Array.from(fileList)) {
      if (count >= MAX_FILES) {
        setFileError(t("onboardingWizard.introFilesLimit"));
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        setFileError(`${file.name}: ${t("onboardingWizard.fileTooLarge")}`);
        continue;
      }
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (ext === "pdf") {
        added.push({ type: "pdf", file, filename: file.name });
        count++;
      } else if (ext === "txt" || ext === "md") {
        // Stesso ponte di StepSources: il backend ingerisce file solo come
        // PDF, quindi txt/md diventano sorgenti di testo lette client-side.
        const text = await file.text();
        if (text.trim().length < 10) {
          setFileError(`${file.name}: ${t("onboardingWizard.fileTooShort")}`);
          continue;
        }
        added.push({
          type: "text",
          title: file.name,
          inline_text: text.slice(0, MAX_INLINE_TEXT),
          fromFile: true,
          fileExt: ext,
          sizeBytes: file.size,
        });
        count++;
      } else {
        setFileError(`${file.name}: ${t("onboardingWizard.docNotSupported")}`);
      }
    }
    if (added.length) setFiles((prev) => [...prev, ...added]);
  };

  const removeFile = (index) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      description: description.trim(),
      websiteUrl: siteOpen && urlValid ? cleanedUrl : "",
      fileSources: files,
    });
  };

  // Il form si invia con l'Invio (come i campi di testo classici) oltre che
  // al click sulla CTA — niente ricarica di pagina, la logica resta la stessa.
  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  // La textarea multi-riga non invia il form con Invio di default (va a
  // capo): Invio da solo invia come i campi singola riga, Shift+Invio va a
  // capo per chi sta scrivendo un racconto più lungo.
  const handleDescriptionKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className={`relative flex flex-col items-center overflow-hidden px-4 ${
        inShell
          ? "-mx-4 -mb-4 min-h-[calc(100vh-4rem)] pt-[9vh]"
          : // A tutta pagina il prompt sta al centro dello schermo, un po' sotto
            // la meta' (come Lovable): pt-20 lascia lo spazio dell'header.
            "min-h-screen justify-center pt-20"
      }`}
      style={{
        backgroundImage:
          "linear-gradient(146deg, #F0F4FF 0%, #F0FFF4 50%, #F8F0FF 100%)",
      }}
    >
      <style>{`
        @keyframes puBlobA { from { transform: translate(0,0) scale(1); } to { transform: translate(60px,40px) scale(1.15); } }
        @keyframes puBlobB { from { transform: translate(0,0) scale(1.1); } to { transform: translate(-50px,-60px) scale(0.95); } }
        @keyframes puBlobC { from { transform: translate(0,0) scale(0.95); } to { transform: translate(40px,50px) scale(1.12); } }
        .pu-blob-a { animation: puBlobA 16s ease-in-out infinite alternate; }
        .pu-blob-b { animation: puBlobB 18s ease-in-out infinite alternate; }
        .pu-blob-c { animation: puBlobC 20s ease-in-out infinite alternate; }
        @media (prefers-reduced-motion: reduce) {
          .pu-blob-a, .pu-blob-b, .pu-blob-c { animation: none; }
        }
      `}</style>

      {/* Blob sfocati del mesh gradient (stessi del loader di creazione) */}
      <div
        className="pu-blob-a pointer-events-none absolute left-[11%] top-[23%] size-[359px] rounded-full opacity-15 blur-[57px]"
        style={{ backgroundImage: "linear-gradient(135deg, #4285F4, #9C4EFF)" }}
      />
      <div
        className="pu-blob-b pointer-events-none absolute left-[82%] top-[58%] size-[309px] rounded-full opacity-15 blur-[57px]"
        style={{ backgroundImage: "linear-gradient(135deg, #34A853, #0F9D58)" }}
      />
      <div
        className="pu-blob-c pointer-events-none absolute left-[52%] top-[4%] size-[338px] rounded-full opacity-15 blur-[72px]"
        style={{ backgroundImage: "linear-gradient(135deg, #9C4EFF, #4285F4)" }}
      />

      {/* Hero */}
      <h1 className="relative max-w-3xl text-center text-[38px] font-semibold leading-[1.15] tracking-tight text-[#0a0a0a] md:text-[44px]">
        {t("onboardingWizard.introTitle")}{" "}
        <Sparkles className="inline h-8 w-8 align-baseline text-[#AD46FF]" />
      </h1>
      <p className="relative mt-4 max-w-xl text-center text-base leading-7 text-muted-foreground">
        {t("onboardingWizard.introSubtitle")}
      </p>

      {/* Card prompt: textarea, "+" a sinistra e CTA a destra */}
      <form
        onSubmit={handleFormSubmit}
        className="relative mt-9 w-full max-w-[760px] rounded-2xl bg-white p-4 shadow-[0_10px_7.5px_rgba(0,0,0,0.05),0_4px_3px_rgba(0,0,0,0.05)]"
      >
        <div className="relative">
          <textarea
            value={description}
            maxLength={MAX_DESCRIPTION}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={handleDescriptionKeyDown}
            placeholder={placeholder}
            className="min-h-[72px] w-full resize-none border-0 bg-transparent text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/80"
          />
          <span className="pointer-events-none absolute -bottom-1 right-0 text-[11px] text-muted-foreground/70">
            {description.length}/{MAX_DESCRIPTION}
          </span>
        </div>

        {/* Input URL (dal "+"): pannello morbido con l'accento viola
            dell'azione sito, coerente col linguaggio delle card */}
        {siteOpen && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/60 px-2.5 py-1.5 transition-colors focus-within:border-violet-400 focus-within:bg-violet-50">
            <Globe className="h-3.5 w-3.5 shrink-0 text-violet-500" />
            <span className="shrink-0 text-xs text-muted-foreground">
              https://
            </span>
            <input
              value={websiteUrl}
              onChange={(e) => {
                setSiteUserManaged(true);
                setWebsiteUrl(e.target.value);
              }}
              placeholder={t("onboardingWizard.introSitePlaceholder")}
              // Focus automatico solo quando è l'utente ad aprire il pannello
              // a mano: se è comparso da solo (URL rilevato nel testo) non
              // deve rubare il focus alla textarea mentre sta ancora scrivendo.
              autoFocus={siteUserManaged}
              className="h-5 min-w-0 flex-1 border-0 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground/60"
            />
            {urlValid && (
              <Check className="h-3.5 w-3.5 shrink-0 text-[#19D163]" />
            )}
            {urlInvalid && (
              <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-red-500" />
            )}
            <button
              type="button"
              onClick={() => {
                setSiteUserManaged(true);
                setSiteOpen(false);
                setWebsiteUrl("");
              }}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={t("onboardingWizard.introRemoveFile")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Documenti allegati */}
        {(files.length > 0 || fileError) && (
          <div className="mt-3">
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-foreground"
                  >
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    {f.filename || f.title}
                    <span className="text-muted-foreground">
                      {formatSize(f.file?.size ?? f.sizeBytes)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="ml-0.5 text-muted-foreground hover:text-foreground"
                      aria-label={t("onboardingWizard.introRemoveFile")}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {fileError && (
              <p className="mt-2 text-xs text-red-600">{fileError}</p>
            )}
          </div>
        )}

        {/* Riga finale: chip sito e documenti a sinistra, CTA a destra */}
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setSiteUserManaged(true);
                setSiteOpen((v) => !v);
              }}
              className={`h-7 rounded-full px-2.5 text-xs font-normal ${
                siteOpen ? "border-violet-400 bg-violet-50/60" : ""
              }`}
            >
              <Globe className="mr-1 h-3.5 w-3.5 text-violet-500" />
              {t("onboardingWizard.introAddSite")}
              <span className="ml-1.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                {t("onboardingWizard.autoBadge")}
              </span>
            </Button>
            {/*
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-full px-2.5 text-xs font-normal"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
              {t("onboardingWizard.introAttachDocs")}
            </Button>
            */}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />

          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-10 rounded-lg bg-[#AD46FF] px-5 text-white hover:bg-[#9d3ee6]"
          >
            {t("onboardingWizard.introCta")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </form>

      <p className="relative mt-5 flex items-center gap-1.5 pb-3 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5" />
        {t("onboardingWizard.introDataSafe")}
        <span className="mx-1">•</span>
        {t("onboardingWizard.introEditable")}
      </p>

      <button
        type="button"
        onClick={onManual}
        className="relative mb-8 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        {t("onboardingWizard.introManualLink")}
      </button>
    </div>
  );
};

export default PromptIntro;
